/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { action, computed } from '@ember/object';
import { inject as service } from '@ember/service';
import { and, equal } from '@ember/object/computed';
import Component from '@glimmer/component';
import { isNone } from '@ember/utils';
import Refinement from 'cio/app-models/conditions/refinement';
import { SUBJECT_EMAIL_FILTERS as EMAIL_FILTERS } from 'cio/utils/email-filters';
import { isDeliveryMeasureEnabledForChannel } from 'cio/utils/metrics/measure';
import config from 'cio/config/environment';
import { stringIncludes } from 'cio/utils/string';
import { task, timeout } from 'ember-concurrency';
import { groupMessages, orderMessages } from 'cio/utils/conditions/action-grouping';
import FiltersService from 'cio/services/filters';
import FeaturesService from 'ember-feature-flags/services/features';
import EmailCondition from 'cio/app-models/conditions/email';
import { tracked } from '@glimmer/tracking';
import AuthService from 'cio/services/auth';
import { getConditionCampaignFirstStarted } from 'cio/utils/conditions';

interface CioConditionEditorEmailArgs {
  // backwards compat
  classNames: string;
  condition: EmailCondition;
  allowMessageWildcardSelection?: boolean;
  allowInParentWildcardSelection?: boolean;
  inverseOptions: { value: boolean; label: string }[];
  specificLinkOptions: { value: boolean; label: string }[];
  depth: number;
  actionItems: { value: boolean; label: string }[];
  customerItems: { value: boolean; label: string }[];
  changetype: () => unknown;
  onRemove: () => unknown;
  disableRefinement?: boolean;
  disabled: boolean;
}

const PER_PAGE = 50;
const DEBOUNCE_DELAY = config.APP.shortDelay;

export default class CioConditionEditorEmail extends Component<CioConditionEditorEmailArgs> {
  @service auth!: AuthService;
  @service filters!: FiltersService;
  @service features!: FeaturesService;

  constructor(owner: unknown, args: CioConditionEditorEmailArgs) {
    super(owner, args);
    this.args.condition.set(
      'campaignFirstStarted',
      getConditionCampaignFirstStarted(this.args.condition, this.filters),
    );
  }

  get inverseOptions() {
    return (
      this.args.inverseOptions ?? [
        { value: false, label: 'has ever' },
        { value: true, label: 'has not ever' },
      ]
    );
  }

  @computed('filters.segmentable.[]', 'args.condition.{email,isAny}')
  get isDeleted() {
    if (this.args.condition?.email == null) {
      return false;
    }

    let segmentableEmails = this.filters.segmentable.filterBy('type', 'email');
    let outputId = this.args.condition.email;

    return !this.args.condition.isAny && isNone(segmentableEmails.findBy('output_id', outputId));
  }

  @computed('orderedMessages.[]', 'args.condition.email')
  get selectedMessage() {
    if (this.args.condition?.email == null) {
      return null;
    }

    return this.orderedMessages.find((m) => m.output_id === this.args.condition.email);
  }

  @computed('args.condition.eventType')
  get emailFilters() {
    return EMAIL_FILTERS.filter(
      // Filter out open if it's disabled on the workspace, unless it's already
      // using it, in which case, we leave it for backwards compat.
      (filter) =>
        !(
          filter.value === 'opened_email' &&
          !isDeliveryMeasureEnabledForChannel('opened', 'email') &&
          this.args.condition.eventType !== 'opened_email'
        ),
    );
  }
  @computed('filters.segmentable.[]', 'args.condition.email')
  get orderedMessages() {
    return orderMessages(
      this.filters.segmentable.filterBy('type', 'email'),
      this.args.condition?.email,
      this.isDeleted,
      true,
      true,
      'email',
    ).map((message) => ({
      ...message,
      subject: message.subject === '' ? message.searchLabel : message.subject, // ensure there's a search label present for the dropdown
    }));
  }

  get groupedMessages() {
    return groupMessages(this.orderedMessages);
  }

  @computed('selectedMessage.isAnyInParent', 'args.{specificLinkOptions,condition.specificLink}')
  get specificLinkOptions() {
    if (this.args.specificLinkOptions != null) {
      return this.args.specificLinkOptions;
    }

    let options = [{ value: false, label: 'on any tracked link' }];

    if (
      !this.selectedMessage?.isAnyInParent ||
      (this.selectedMessage?.isAnyInParent && this.args.condition.specificLink === true)
    ) {
      options = [...options, { value: true, label: 'on tracked link matching' }];
    }

    return options;
  }

  @equal('args.condition.eventType', 'clicked_email') showSpecificLink!: boolean;
  @and('showSpecificLink', 'args.condition.specificLink') showLinkHref!: boolean;

  @action
  addRefinement() {
    this.args.condition.set('refinement', Refinement.create());
  }

  @action
  removeRefinement() {
    this.args.condition.set('refinement', null);
  }

  @action
  onEmailChange(option: Record<string, any>) {
    this.args.condition.setProperties({
      email: option.output_id,
      campaignFirstStarted: option.parent?.campaignFirstStarted,
    });

    if (!this.args.condition.canRefine && this.args.condition.refinement != null) {
      this.args.condition.set('refinement', null);
    }
  }

  // Potentially, there may be thousands of messages shown in the dropdown. We
  // don't want to render them all at once, so we'll use the fly-power-select
  // component's "search" and "onScrollEnd" functionalities to perform a
  // "virtual" query. We don't hit an API during these searches, we'll perform
  // the filtering right here in JavaScript.

  @tracked virtualSelectPage = 1;
  @tracked previousSearchText = '';

  get initialSelectOptions() {
    return groupMessages(this.orderedMessages.slice(0, PER_PAGE));
  }

  @action
  virtualQuery(searchText: string, page: number) {
    let start = PER_PAGE * (page - 1);

    return groupMessages(
      this.orderedMessages
        .filter((message) => stringIncludes(message.searchLabel ?? '', searchText))
        .slice(start, start + PER_PAGE),
    );
  }

  selectOnScrollEnd = task({ restartable: true }, async (searchText: string) => {
    // ember power select doesn't call the "search" function when resetting
    // search back to an empty string, which can mess up our "virtual page"
    // tracking.
    // If the page is larger than 1, and the new search text is an empty string,
    // but the old search text wasn't, it means that the user has emptied the
    // search box, and then scrolled to the end of the list. We need to reset
    // the page back to the start in this case.
    if (this.virtualSelectPage > 1 && searchText === '' && this.previousSearchText !== searchText) {
      this.virtualSelectPage = 1;
    }

    this.virtualSelectPage = this.virtualSelectPage + 1;
    this.previousSearchText = searchText;

    return this.virtualQuery(searchText, this.virtualSelectPage);
  });

  paginatedSelectQuery = task({ restartable: true }, async (searchText: string) => {
    this.virtualSelectPage = 1;
    this.previousSearchText = searchText;

    await timeout(DEBOUNCE_DELAY);
    return this.virtualQuery(searchText, this.virtualSelectPage);
  });
}
